import React, { useState, useEffect, FC } from "react";
import { Button, Badge, Row, Col } from "reactstrap";
import {
  Table,
  Input,
  Flex,
  Spin,
  Tooltip,
  Drawer,
  Modal,
  Form,
  Popover,
  Timeline,
  Select,
  DatePicker,
  Space,
  Tag,
  Empty,
  Descriptions,
  Divider,
  Card,
  Statistic,
  Button as AntButton,
} from "antd";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  SearchOutlined,
  EditOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router";
import PaymentsAside from "./PaymentsAside";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import {
  PaymentData,
  PaymentStatus,
  PaymentType,
  PaymentMethod,
  UserRole,
  PaymentFilters,
  PaymentWidgetData,
} from "./types";
import {
  mockPaymentData,
  getPaymentsByStatus,
  getPaymentsByContractor,
  mockPaymentWidgetData,
  mockPaymentStatusOptions,
  mockPaymentMethodOptions,
} from "./mockData/paymentData";
import {
  mockContractorOptions,
  mockSiteOptions,
} from "./mockData/approvedRaBillsData";
import paymentsApis from "../../api/WorkOrderManagement/PaymentsApis";
const ViewPayments: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location?.state?.id;
  const [widgetData, setWidgetData] = useState<any>([]);
  const [sm, setSm] = useState(false);
  const [viewDrawer, setViewDrawer] = useState(false);
  const [drawerData, setDrawerData] = useState<any>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Enhanced payment management state
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentData[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<any>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [paymentWidgetData, setPaymentWidgetData] = useState<PaymentWidgetData>(
    mockPaymentWidgetData
  );

  // Filters
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [searchText, setSearchText] = useState("");

  // Enhanced useEffect hooks
  useEffect(() => {
    loadPayments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [payments, filters, searchText]);

  const loadPayments = async () => {
    try {
      setLoading(true);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Load all payments
      setPayments(mockPaymentData);
      setPaymentWidgetData(mockPaymentWidgetData);
    } catch (error) {
      console.error("Error loading payments:", error);
      Swal.fire("Error!", "Failed to load payments", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    // Apply search text filter
    if (searchText) {
      filtered = filtered.filter(
        (payment) =>
          payment.paymentVoucherNumber
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          payment.contractorName
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          payment.siteName.toLowerCase().includes(searchText.toLowerCase()) ||
          (payment.utrReference &&
            payment.utrReference
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          payment.allocations.some(
            (alloc) =>
              alloc.raBillNumber
                ?.toLowerCase()
                .includes(searchText.toLowerCase()) ||
              alloc.workOrderNumber
                ?.toLowerCase()
                .includes(searchText.toLowerCase())
          )
      );
    }

    // Apply date range filters
    if (
      filters.paymentDateRange &&
      filters.paymentDateRange[0] &&
      filters.paymentDateRange[1]
    ) {
      filtered = filtered.filter((payment) => {
        const paymentDate = dayjs(payment.paymentDate);
        return (
          paymentDate.isAfter(filters.paymentDateRange![0]) &&
          paymentDate.isBefore(filters.paymentDateRange![1])
        );
      });
    }

    // Apply contractor filter
    if (filters.contractorIds && filters.contractorIds?.length > 0) {
      filtered = filtered.filter((payment) =>
        filters.contractorIds!.includes(payment.contractorId)
      );
    }

    // Apply site filter
    if (filters.siteIds && filters.siteIds?.length > 0) {
      filtered = filtered.filter((payment) =>
        filters.siteIds!.includes(payment.siteId)
      );
    }

    // Apply status filter
    if (filters.statuses && filters.statuses?.length > 0) {
      filtered = filtered.filter((payment) =>
        filters.statuses!.includes(payment.status)
      );
    }

    // Apply payment method filter
    if (filters.paymentMethods && filters.paymentMethods?.length > 0) {
      filtered = filtered.filter((payment) =>
        filters.paymentMethods!.includes(payment.paymentMethod)
      );
    }

    // Apply amount range filter
    if (filters.amountRange) {
      filtered = filtered.filter(
        (payment) =>
          payment.netPayable >= (filters.amountRange!.min || 0) &&
          payment.netPayable <= (filters.amountRange!.max || Infinity)
      );
    }

    setFilteredPayments(filtered);
  };

  const getStatusColor = (status: PaymentStatus): string => {
    const colors: Record<PaymentStatus, string> = {
      [PaymentStatus.Draft]: "default",
      [PaymentStatus.Submitted]: "processing",
      [PaymentStatus.Checked]: "warning",
      [PaymentStatus.Verified]: "orange",
      [PaymentStatus.Approved]: "success",
      [PaymentStatus.Posted]: "cyan",
      [PaymentStatus.Paid]: "green",
      [PaymentStatus.Rejected]: "error",
      [PaymentStatus.Returned]: "purple",
    };
    return colors[status] || "default";
  };

  const handleViewPayment = async (payment: any) => {
    try {
      setLoading(true);
      console.log("Loading payment details for ID:", payment.id);

      const response: any = await paymentsApis.paymentById(
        payment.raBillPaymentId
      );
      const { data, status, message } = response;
      if (status) {
        // Map API response to payment data structure
        const processedPayment = {
          ...payment,
          // Map API response fields to payment object
          paymentVoucherNumber: data.raBillPaymentSequenceId,
          paymentDate: data.paymentDate,
          paymentType: data.paymentType,
          status: data.status,
          contractorName: data.contractorName,
          siteName: data.siteName,
          paymentMethod: data.paymentMethod,
          bankAccountNumber: data.bankAccountNumber,
          bankName: data.bankName,
          totalPaymentAmount: data.totalPaymentAmount,
          paidAmount: data.paidAmount,
          netPayableAmount: data.netPayableAmount,
          tdsSection: data.tdsSection,
          tdsRate: data.tdsRate,
          tdsDeducted: data.tdsDeducted,
          otherDeductions: data.otherDeductions,
          utrReference: data.utrNumber,
          narration: data.narration,
          remarks: data.remarks,

          // Process payment lines (allocations)
          allocations:
            data.raBillPaymentLines?.map((line: any) => ({
              id: line.raBillPaymentLineId,
              raBillId: line.raBillId,
              raBillNumber: line.raBillSequenceId,
              workOrderId: line.workOrderId,
              workOrderNumber: line.workOrderSequenceId || line.workOrderNumber,
              thisPaymentAmount: line.paidAmount,
              resultingStatus: line.status,
              totalPaidAmount: line.totalPaidAmount,
              raBillNetPayable: line.raBillNetPayable,
              raPeriodFrom: line.raPeriodFrom,
              raPeriodTo: line.raPeriodTo,
            })) || [],

          // Process approval history
          approvalHistory:
            data.paymentApprovals?.map((approval: any) => ({
              action:
                approval.status === "Approved"
                  ? "Approved"
                  : approval.status === "Rejected"
                  ? "Rejected"
                  : "Pending",
              actionBy: approval.approvedByName || approval.approverName,
              actionDate: approval.approvalDate || approval.createdAt,
              role: approval.approvalStage,
              status: approval.status,
              comments: approval.reason,
              level: approval.level,
            })) || [],

          // Process attachments/documents
          attachments: data.dms || [],

          // Additional details
          createdBy: data.createdByName,
          createdAt: data.createdAt,
          processedBy: data.processedByName,
          processedAt: data.processedAt,
          approvedBy: data.approvedByName,
          approvedAt: data.approvedAt,
        };

        setSelectedPayment(processedPayment);
        setShowDetailsModal(true);
      } else {
        throw new Error("No payment data found");
      }
    } catch (error) {
      console.error("Error loading payment details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load payment details. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayment = (payment: PaymentData) => {
    navigate(`/payments/create-payment?edit=${payment.id}`);
  };

  const handleDuplicatePayment = (payment: PaymentData) => {
    navigate(`/payments/create-payment?duplicate=${payment.id}`);
  };

  // Enhanced columns for new payment data
  const enhancedColumns: any = [
    {
      title: "#",
      key: "#",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Payment Voucher",
      dataIndex: "raBillPaymentSequenceId",
      key: "raBillPaymentSequenceId",
      width: 220,
      fixed: "left",
      render: (text: string, record: PaymentData) => (
        <div>
          <div className="fw-bold text-primary">{text}</div>
          <small className="text-muted">{record.paymentType} Payment</small>
        </div>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    {
      title: "Contractor",
      key: "contractor",
      width: 200,
      render: (record: PaymentData) => (
        <div>
          <div className="fw-bold">{record.contractorName}</div>
          <small className="text-muted">{record.siteName}</small>
        </div>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 160,
      render: (method: PaymentMethod) => <Tag color="blue">{method}</Tag>,
    },
    {
      title: "Net Amount",
      dataIndex: "netPayableAmount",
      key: "netPayableAmount",
      width: 120,
      render: (amount: number) => (
        <div className="fw-bold text-success">₹{amount?.toLocaleString()}</div>
      ),
    },
    {
      title: "UTR Reference",
      dataIndex: "utrNumber",
      key: "utrNumber",
      width: 140,
      render: (utr: string) =>
        utr ? (
          <Tooltip title={utr}>
            <span>{utr.substring(0, 12)}...</span>
          </Tooltip>
        ) : (
          <span className="text-muted">--</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: PaymentStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    // {
    //   title: "RA Bills",
    //   key: "raBills",
    //   width: 80,
    //   render: (record: PaymentData) => (
    //     <span className="badge badge-dim badge-blue">
    //       {record?.allocations?.length}
    //     </span>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (record: PaymentData) => (
        <Space>
          <Tooltip title="View Details">
            <AntButton
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewPayment(record)}
            />
          </Tooltip>
          {/* {record.status === PaymentStatus.Draft && (
            <Tooltip title="Edit">
              <AntButton
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEditPayment(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Duplicate">
            <AntButton
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleDuplicatePayment(record)}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];


  const ApprovalTimelineContent = (approvalsData: any) => {
    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No Approvals</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status == "Approved";
              const isRejected = approval.status == "Rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.approvalDate && (
                    <div className="designation">
                      {dayjs(approval?.approvalDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2263b3",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          <Badge color="outline-success">View</Badge>
        </span>
      </Popover>
    );
  };


  const disapprovePayment = async (reason: any) => {
    // try {
    //   const res = await paymentApis.disapprovePayment(
    //     drawerData?.paymentNumber,
    //     reason
    //   );
    //   const { status, message } = res;
    //   if (status) {
    //     Swal.fire("Success", message, "success");
    //     // fetchData();
    //   }
    // } catch (error) {}
  };

  const { Search } = Input;
  const { RangePicker } = DatePicker;
  const { Option } = Select;

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="view-payments"
        title="All Payments"
        columns={enhancedColumns} // Use enhanced columns
        loading={loading}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <PaymentsAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1200,
        }}
      />

      {/* Enhanced Payment Details Modal */}
       <Modal
        title={
          <div className="d-flex align-items-center">
            <FileTextOutlined className="mr-2" />
            Payment Details - {selectedPayment?.paymentVoucherNumber}
          </div>
        }
        open={showDetailsModal}
        onCancel={() => setShowDetailsModal(false)}
        width={1500}
        footer={[
          <AntButton key="close" onClick={() => setShowDetailsModal(false)}>
            Close
          </AntButton>
        ].filter(Boolean)}
      >
        {selectedPayment && (
          <div>
            <Row gutter={[24, 24]}>
              <Col span={20}>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Payment Voucher">
                    {selectedPayment.paymentVoucherNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Date">
                    {dayjs(selectedPayment.paymentDate).format("DD/MM/YYYY")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Type">
                    {selectedPayment.paymentType}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedPayment.status)}>
                      {selectedPayment.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Contractor">
                    {selectedPayment.contractorName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Site">
                    {selectedPayment.siteName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Legal Entity">
                    {selectedPayment.legalEntityName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Method">
                    {selectedPayment.paymentMethod}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bank Account">
                    {selectedPayment.bankAccountNumber && selectedPayment.bankName 
                      ? `${selectedPayment.bankAccountNumber} - ${selectedPayment.bankName}`
                      : (selectedPayment.bankAccountName || "N/A")}
                  </Descriptions.Item>
                  <Descriptions.Item label="TDS Section">
                    {selectedPayment.tdsSection} ({selectedPayment.tdsRate}%)
                  </Descriptions.Item>
                  <Descriptions.Item label="TDS Deducted">
                    ₹{selectedPayment.tdsDeducted?.toLocaleString() || "0"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Other Deductions">
                    ₹{selectedPayment.otherDeductions?.toLocaleString() || "0"}
                  </Descriptions.Item>
                  {selectedPayment.utrReference && (
                    <Descriptions.Item label="UTR Reference" span={2}>
                      <code>{selectedPayment.utrReference}</code>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Narration" span={2}>
                    {selectedPayment.narration || "No narration provided"}
                  </Descriptions.Item>
                  {selectedPayment.remarks && (
                    <Descriptions.Item label="Remarks" span={2}>
                      {selectedPayment.remarks}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Created By">
                    {selectedPayment.createdBy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {selectedPayment.createdAt ? dayjs(selectedPayment.createdAt).format("DD/MM/YYYY HH:mm") : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Net Payable Amount"
                    value={selectedPayment.netPayableAmount || selectedPayment.netPayable}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: "#3f8600" }}
                  />
                  <Divider />
                  <div className="d-flex justify-content-between">
                    <span>Total Payment Amount:</span>
                    <strong>
                      ₹{selectedPayment.totalPaymentAmount?.toLocaleString()}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Paid Amount:</span>
                    <strong>
                      ₹{selectedPayment.paidAmount?.toLocaleString()}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>TDS Deducted:</span>
                    <strong className="text-warning">
                      -₹{selectedPayment.tdsDeducted?.toLocaleString() || "0"}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Other Deductions:</span>
                    <strong>
                      -₹{selectedPayment.otherDeductions?.toLocaleString() || "0"}
                    </strong>
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider />

            <h6>Payment Allocations ({selectedPayment?.allocations?.length})</h6>
            <Table
              columns={[
                {
                  title: "RA Bill Number",
                  dataIndex: "raBillNumber",
                  key: "raBillNumber",
                  width: 140,
                },
                {
                  title: "Work Order",
                  dataIndex: "workOrderNumber",
                  key: "workOrderNumber",
                  width: 140,
                },
                {
                  title: "RA Period",
                  dataIndex: "raPeriod",
                  key: "raPeriod",
                  width: 180,
                  render:(data,record:any) => `${record?.raPeriodFrom} to ${record?.raPeriodFrom}`
                },
                {
                  title: "Bill Value",
                  dataIndex: "raBillNetPayable",
                  key: "raBillNetPayable",
                  width: 120,
                  render: (amount: number) => `₹${amount?.toLocaleString()}`,
                },
                {
                  title: "Already Paid",
                  dataIndex: "totalPaidAmount",
                  key: "totalPaidAmount",
                  width: 120,
                  render: (amount: number) => `₹${amount?.toLocaleString()}`,
                },
                {
                  title: "This Payment",
                  dataIndex: "thisPaymentAmount",
                  key: "thisPaymentAmount",
                  width: 120,
                  render: (amount: number) => `₹${amount?.toLocaleString()}`,
                },
                {
                  title: "Resulting Status",
                  dataIndex: "resultingStatus",
                  key: "resultingStatus",
                  width: 120,
                  render: (status: string) => {
                    const color =
                      status === "Paid"
                        ? "green"
                        : status === "Partially Paid"
                        ? "orange"
                        : "red";
                    return <Tag color={color}>{status}</Tag>;
                  },
                },
              ]}
              dataSource={selectedPayment.allocations}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 800 }}
            />

            <Divider />

            {/* Attachments Section */}
            {selectedPayment.attachments && selectedPayment.attachments.length > 0 && (
              <>
                <h6>Supporting Documents ({selectedPayment.attachments.length})</h6>
                <div style={{ marginBottom: 16 }}>
                  {selectedPayment.attachments.map((attachment: any, index: number) => (
                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FileTextOutlined className="mr-2" />
                          <strong>{attachment.fileName || attachment.originalFileName || `Document ${index + 1}`}</strong>
                          {attachment.fileSize && (
                            <span className="text-muted ml-2">
                              ({(attachment.fileSize / 1024).toFixed(1)} KB)
                            </span>
                          )}
                        </div>
                        <div>
                          {attachment.uploadDate && (
                            <span className="text-muted mr-2">
                              {dayjs(attachment.uploadDate).format("DD/MM/YYYY")}
                            </span>
                          )}
                          {attachment.fileUrl && (
                            <AntButton
                              size="small"
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={() => window.open(attachment.fileUrl, '_blank')}
                            >
                              View
                            </AntButton>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                <Divider />
              </>
            )}

            <h6>Approval History</h6>
            <Timeline>
              {selectedPayment.approvalHistory.map((history:any, index:any) => (
                <Timeline.Item
                  key={index}
                  color={
                    history.status === PaymentStatus.Rejected ? "red" : "blue"
                  }
                >
                  <div>
                    <strong>{history.action}</strong> by {history.actionBy}
                    <div className="text-muted small">
                      {dayjs(history.actionDate).format("DD/MM/YYYY HH:mm")} |{" "}
                      {history.role}
                    </div>
                    {history.comments && (
                      <div className="mt-1 p-2 bg-light rounded">
                        <small>{history.comments}</small>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        )}
      </Modal>
      <DisapprovePayment
        isModalVisible={showRejectModal}
        setIsModalVisible={setShowRejectModal}
        reject={disapprovePayment}
      />
    </>
  );
};

const DisapprovePayment = ({
  isModalVisible,
  setIsModalVisible,
  reject,
}: any) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        reject(reason); // Call your reject function here
        form.resetFields();
        setIsModalVisible(false);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <Modal
      title="Reason for Rejection"
      open={isModalVisible}
      footer={null} // Remove the default footer buttons
      onCancel={handleCancel}
    >
      <Form
        form={form}
        layout="vertical"
        name="rejectForm"
        requiredMark={false}
      >
        <Form.Item
          name="reason"
          label={undefined}
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." />
        </Form.Item>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit" onClick={handleOk}>
                Submit
              </Button>
            </li>
            <li>
              <Button
                color="primary"
                onClick={handleCancel}
                className="btn-dim btn btn-secondary"
              >
                Cancel
              </Button>
            </li>
          </ul>
        </div>
      </Form>
    </Modal>
  );
};

export default ViewPayments;
